import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Share2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const sel = "h-9 w-full rounded-lg border border-input bg-white px-2 text-sm";
const num = (v) => (v === "" || v === undefined || v === null ? null : Number(v));

export function SendToPoolButton({ customer }) {
  const [open, setOpen] = useState(false);
  const [txt, setTxt] = useState(null);
  const [f, setF] = useState(null);
  const [consent, setConsent] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/pool/consent-text").then((r) => setTxt(r.data));
    setF({ city: customer.desired_city || "", district: customer.desired_district || "", neighborhood: customer.desired_neighborhood || "",
      listing_type: customer.desired_listing_type === "RENT" ? "RENT" : "SALE", property_type: "", budget_min: customer.budget_min ?? "", budget_max: customer.budget_max ?? "",
      rooms: "", area_min: "", area_max: "", building_age_max: "", floor_pref: "ANY", heating: "", extra: "" });
  }, [open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true);
    try {
      const criteria = { ...f, budget_min: num(f.budget_min), budget_max: num(f.budget_max), area_min: num(f.area_min), area_max: num(f.area_max),
        building_age_max: num(f.building_age_max), rooms: f.rooms.split(",").map((x) => x.trim()).filter(Boolean),
        district: f.district || null, neighborhood: f.neighborhood || null, property_type: f.property_type || null, heating: f.heating || null, extra: f.extra || null };
      const r = await api.post("/pool/requests", { customer_id: customer.id, criteria, consent_declared: consent });
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/pool/requests/${r.data.id}/consent-doc`, fd).catch(() => toast.warning("Talep havuza eklendi ancak izin belgesi yüklenemedi. Belgeyi daha sonra tekrar yükleyebilirsiniz."));
      }
      toast.success(`Havuza gönderildi (${r.data.anon_no}) · ${r.data.matches} eşleşme bulundu`);
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const field = (k, label, props = {}) => <label className="block"><span className="text-xs font-semibold text-slate-600">{label}</span><Input data-testid={`pool-${k}`} value={f[k]} onChange={set(k)} {...props} /></label>;
  return (
    <>
      <Button data-testid="send-to-pool-btn" size="sm" variant="outline" onClick={() => setOpen(true)}><Share2 className="w-4 h-4 mr-1.5" />CRM Havuzuna Gönder</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="send-to-pool-dialog">
          <DialogHeader><DialogTitle>CRM Havuzuna Gönder</DialogTitle></DialogHeader>
          {f && (
            <div className="space-y-4">
              <p className="text-xs text-slate-500">Yalnızca aşağıdaki arama kriterleri paylaşılır. Müşterinin adı, telefonu ve e-postası diğer ofislere gösterilmez.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {field("city", "Şehir *")}{field("district", "İlçe")}{field("neighborhood", "Mahalle")}
                <label className="block"><span className="text-xs font-semibold text-slate-600">Satılık / Kiralık *</span>
                  <select data-testid="pool-listing_type" className={sel} value={f.listing_type} onChange={set("listing_type")}><option value="SALE">Satılık</option><option value="RENT">Kiralık</option></select></label>
                {field("property_type", "Taşınmaz türü (ör. Daire)")}{field("rooms", "Oda sayısı (ör. 2+1, 3+1)")}
                {field("budget_min", "Min. bütçe (TL)", { type: "number" })}{field("budget_max", "Maks. bütçe (TL)", { type: "number" })}{field("building_age_max", "En fazla bina yaşı", { type: "number" })}
                {field("area_min", "Min. m²", { type: "number" })}{field("area_max", "Maks. m²", { type: "number" })}
                <label className="block"><span className="text-xs font-semibold text-slate-600">Kat tercihi</span>
                  <select data-testid="pool-floor_pref" className={sel} value={f.floor_pref} onChange={set("floor_pref")}>
                    {[["ANY", "Fark etmez"], ["GROUND", "Giriş / bahçe"], ["NOT_GROUND", "Giriş olmasın"], ["MIDDLE", "Ara kat"], ["TOP", "En üst kat"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select></label>
                {field("heating", "Isıtma türü")}
              </div>
              {field("extra", "Diğer özel talepler (kişisel bilgi yazmayın)")}
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="pool-consent-text">
                <b>Aydınlatma metni ({txt?.consent_version}):</b> {txt?.consent_text}
              </div>
              <label className="flex items-start gap-2 text-sm"><Checkbox data-testid="pool-consent-checkbox" checked={consent} onCheckedChange={(v) => setConsent(!!v)} />
                <span>Aydınlatma metnini müşteriye okuduğumu ve müşterinin kriterlerinin havuzda paylaşılmasına izin verdiğini beyan ederim.</span></label>
              <label className="block text-xs text-slate-600">İmzalı izin belgesi (isteğe bağlı, PDF/görsel)
                <input data-testid="pool-consent-file" type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block mt-1 text-xs" /></label>
              <Button data-testid="pool-submit-btn" disabled={busy || !consent || !f.city} onClick={submit} className="w-full">Havuza Gönder</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
